import { useState, useRef, useEffect } from "react";
import styles from "./ReviewPhotoGallery.scss";
import getWindow from "../../utils/window";
import BrandedSpinner from "../spinner/branded/BrandedSpinner";
import useTrapFocus from "../../hooks/useTrapFocus";
import useEventListener from "../../hooks/useEventListener";
import { useRuntimeConstants } from "../../context/runtimeConstants/RuntimeConstantsContext";
import ImageGallery from "../imageGallery/ImageGallery";
import { ImageGalleryProvider } from "../imageGallery/ImageGalleryContext";
import { mapPhotoGalleryProps } from "./mapPhotoGalleryProps";
import { ReviewPhotoGalleryComponentProps } from "../../types/components/reviewPhotoGallery";
import { MapImageArgs } from "../../types/components/imageGallery";

export default function ReviewPhotoGallery({
  togglePhotoGallery,
  gallery,
  initialIndex = 0,
  screenHoverable,
}: ReviewPhotoGalleryComponentProps) {
  const [overlayId] = useState(`overlay${Math.floor(Math.random() * 10000)}`);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [galleryWidth, setGalleryWidth] = useState(0);
  const backButtonRef = useRef<HTMLButtonElement>(null);
  const { getTranslation } = useRuntimeConstants() as {
    getTranslation: (key: string, params?: Record<string, unknown>) => string;
  };

  const handleKeyDown = (event: KeyboardEvent) => {
    if (event.keyCode === 27 || event.key === "escape") {
      togglePhotoGallery(false);
    }
  };

  useEffect(() => {
    backButtonRef.current?.focus();
    const sideModal = getWindow().document.querySelector(
      "[data-testid='sideModal']",
    );
    const sideModalWidth = sideModal!.getBoundingClientRect().width;

    setGalleryWidth(sideModalWidth);
  }, []);

  useEventListener("keydown", handleKeyDown, []);

  const setModalRef = useTrapFocus();

  const mapImage = ({
    image,
    index,
    displayedImageIndex,
    ariaHiddenOverride,
  }: MapImageArgs) => {
    return (
      <img
        style={{ width: galleryWidth }}
        key={image.url}
        className={styles.image}
        data-testid="reviewsPhotoGalleryImage"
        alt={`${getTranslation(
          "rdp_ratings_reviews_accessibility_image_description",
        )}. ${getTranslation("pdp_ratings_review_gallery_image_count", {
          index: index + 1,
          total: gallery.reviewPhotoList.length,
        })}`}
        src={image.url}
        onLoad={() => setImageLoaded(true)}
        aria-hidden={ariaHiddenOverride || displayedImageIndex !== index}
      />
    );
  };

  return gallery?.reviewPhotoList.length ? (
    <div className={styles.container} id={overlayId} ref={setModalRef}>
      <button
        className={styles.backBtn}
        data-testid="reviewsPhotoGalleryBackButton"
        aria-label={getTranslation("pdp_misc_back")}
        onClick={() => togglePhotoGallery(false)}
        ref={backButtonRef}
      >
        <div className={"product-left-arrow-outlined"} />
      </button>
      {!imageLoaded && <BrandedSpinner colour={"white"} />}
      <ImageGalleryProvider
        images={mapPhotoGalleryProps(gallery)}
        initialIndex={initialIndex}
      >
        <ImageGallery screenHoverable={screenHoverable} mapImage={mapImage} />
      </ImageGalleryProvider>
    </div>
  ) : null;
}
